import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import KeyIcon from "@mui/icons-material/Key";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Button from "@mui/material/Button";
import axios from "axios";
import { API_PWD_CHANGE } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import { extractServerErrorMessage } from "../../../../utilities/errorParsing";

const axiosConfig = getDefaultAxiosConfig();

function ChangePasswordModal(props) {

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 6,
  };

  const [passwordChangeSubmitted, setPasswordChangeSubmitted] = React.useState(false);
  const [oldPassword, setOldPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [showOld, setShowOld] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const clearError = () => {
    if (errorMessage) setErrorMessage("");
  };

  const handleOldPasswordChange = (event) => {
    setOldPassword(event.target.value);
    clearError();
  };

  const handleNewPasswordChange = (event) => {
    setNewPassword(event.target.value);
    clearError();
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
    clearError();
  };

  const handleChangePassword = async () => {
    if (submitting) return;

    if (!oldPassword) {
      setErrorMessage("Enter your current password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("New passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setErrorMessage("New password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      await axios.post(
        API_PWD_CHANGE,
        {
          old_password: oldPassword,
          new_password1: newPassword,
          new_password2: confirmPassword,
        },
        axiosConfig
      );
      setPasswordChangeSubmitted(true);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 429) {
        setErrorMessage("Too many attempts. Please wait and try again.");
      } else if (status === 400 && data?.old_password) {
        setErrorMessage("Current password is incorrect.");
      } else if (status === 400) {
        setErrorMessage(
          extractServerErrorMessage(data) ||
            "That password isn't accepted. Try one with at least 8 characters that isn't too common or too close to your email."
        );
      } else {
        setErrorMessage(
          "We couldn't change your password right now. Please try again in a few minutes."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  function handleClose() {
    props.handleClose();
    setPasswordChangeSubmitted(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrorMessage("");
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
  }

  const visibilityAdornment = (visible, onToggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={onToggle}
          size="small"
        >
          {visible ? (
            <VisibilityIcon style={{ fontSize: "20px" }} />
          ) : (
            <VisibilityOffIcon style={{ fontSize: "20px" }} />
          )}
        </IconButton>
      </InputAdornment>
    ),
  });

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px !important",
      height: "45px",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6d28d9",
      },
      backgroundColor: "white",
    },
  };

  return (
    <Modal
      open={props.open}
      onClose={() => handleClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="w-full h-full flex flex-col justify-center items-center">
          <div
            className="absolute right-4 top-4 text-gray-500 cursor-pointer"
            onClick={() => handleClose()}
          >
            <CloseIcon />
          </div>
          {!passwordChangeSubmitted ? (
            <div className="w-full flex flex-col items-center justify-center">
              <div className="h-24 w-24 bg-violet-50 flex items-center justify-center rounded-full">
                <div className="h-16 w-16 bg-violet-100 flex items-center justify-center rounded-full text-primary">
                  <KeyIcon style={{ fontSize: 40, color: "#6d28d9" }} />
                </div>
              </div>
              <div className="mt-5 flex flex-col justify-center items-center">
                <h3 className="text-3xl text-slate-900 text-center">
                  Change Password
                </h3>
                <p className="text-slate-500 mt-4 text-sm text-center">
                  Your new password must be different than previously used
                  passwords
                </p>
              </div>
              <div className="w-full mt-8">
                <p className="text-sm text-slate-500">Current password</p>
                <div className="mt-1">
                  <TextField
                    id="old-password"
                    variant="outlined"
                    type={showOld ? "text" : "password"}
                    fullWidth
                    value={oldPassword}
                    onChange={handleOldPasswordChange}
                    autoComplete="current-password"
                    sx={fieldSx}
                    InputProps={visibilityAdornment(showOld, () =>
                      setShowOld(!showOld)
                    )}
                  />
                </div>
              </div>
              <div className="w-full mt-6">
                <p className="text-sm text-slate-500">New password</p>
                <div className="mt-1">
                  <TextField
                    id="new-password"
                    variant="outlined"
                    type={showNew ? "text" : "password"}
                    fullWidth
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    autoComplete="new-password"
                    sx={fieldSx}
                    InputProps={visibilityAdornment(showNew, () =>
                      setShowNew(!showNew)
                    )}
                  />
                </div>
                <p className="text-slate-500 mt-0.5 text-xxs text-end">
                  Must be a minimum of 8 letters
                </p>
              </div>
              <div className="w-full">
                <p className="text-sm text-slate-500">Confirm new password</p>
                <div className="mt-1">
                  <TextField
                    id="confirm-password"
                    variant="outlined"
                    type={showConfirm ? "text" : "password"}
                    fullWidth
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    autoComplete="new-password"
                    sx={fieldSx}
                    InputProps={visibilityAdornment(showConfirm, () =>
                      setShowConfirm(!showConfirm)
                    )}
                  />
                </div>
              </div>
              {errorMessage && (
                <p className="w-full text-rose-600 text-sm mt-3">
                  {errorMessage}
                </p>
              )}
              <Button
                onClick={handleChangePassword}
                disabled={submitting}
                fullWidth
                variant="contained"
                sx={{
                  textTransform: "none",
                  borderRadius: "9999px",
                  paddingY: "14px",
                  marginTop: "32px",
                  backgroundColor: "#6d28d9",
                  "&:hover": { backgroundColor: "#5b21b6" },
                  "&.Mui-disabled": {
                    backgroundColor: "#c4b5fd",
                    color: "white",
                  },
                  fontSize: "16px",
                }}
              >
                <span style={{ color: "white" }}>
                  {submitting ? "Changing…" : "Change Password"}
                </span>
              </Button>
            </div>
          ) : (
            <div className="full flex flex-col items-center justify-center">
              <div className="h-24 w-24 bg-green-50 flex items-center justify-center rounded-full">
                <div className="h-16 w-16 bg-green-100 flex items-center justify-center rounded-full text-green-700">
                  <CheckCircleOutlineIcon style={{ fontSize: 40 }} />
                </div>
              </div>
              <div className="mt-5 flex flex-col justify-center items-center">
                <h3 className="text-3xl text-slate-900">Password Changed</h3>
                <p className="text-slate-500 mt-8 text-base text-center">
                  Your password has been updated. Use it the next time you sign in.
                </p>
              </div>
              <button
                className="w-full bg-violet-700 hover:bg-violet-800 rounded text-white py-4 mt-8"
                onClick={() => handleClose()}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default ChangePasswordModal;
